import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { DESCRIPTION, KIT_TYPE, NAME } from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useAddTaskCategoryMutation } from "@/services/masterData";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useGetTaskCategoryDetailQuery from "@/services/masterData/taskcategory/useTaskCategoryDetailMutation";
import useUpdateTaskCategorysMutation from "@/services/masterData/taskcategory/useUpdateTaskCategoryMutation";
import {useAddKITMutation, useUpdateKITMutation, useGetKITDetailQuery } from "@/services/masterData";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [initialDetailPayload, setInitialDetailPayload] = useState<Record<
    string,
    any
  > | null>(null);

  const { data: detailRes, isPending: isDetailLoading } =
    useGetKITDetailQuery(isEdit || isView ? String(id) : undefined);

  const { mutateAsync: updateKIT, isPending: isUpdating } =
    useUpdateKITMutation({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["kit"] });
        toast.success("KIT updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update KIT"
        );
      },
    });
  const { mutateAsync: addKIT, isPending } =
    useAddKITMutation({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["kit"] });
        toast.success("KIT created successfully");
        onBackClick();
      },
      onError: (error: any) => {
        const errorCode = error?.response?.data?.issues?.errorCode;
        const backendMessage =
          errorCode === "DUPLICATE_TASK_CATEGORY"
            ? "Category name already exists. Please choose a unique name."
            : error?.response?.data?.issues?.message ||
              error?.response?.data?.message ||
              "Failed to create KIT";

        toast.error(backendMessage);
      },
    });

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [KIT_TYPE]: "",
      [DESCRIPTION]: "",
    },
  });

  useEffect(() => {
    if ((isEdit || isView) && detailRes?.data) {
      const d = detailRes.data;
      form.reset({
        [NAME]: String(d?.kitName ?? ""),
        [KIT_TYPE]: String(d?.kitType ?? ""),
        [DESCRIPTION]: d?.description ? String(d?.description) : "",
      });
      setInitialDetailPayload({
        kitName: String(d?.kitName ?? ""),
        kitType: String(d?.kitType ?? ""),
        description: d?.description ? String(d?.description) : undefined,
      });
    }
  }, [detailRes, isEdit, isView, form]);

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    if (isEdit && id) {
      await updateKIT({ id: String(id), data: _data });
    } else {
      await addKIT({ data: _data });
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending,
  };
};

export default useAddEdit;
